import { PHONE_NUMBER } from "@src/constants";
import { useOptionalApplicationContext } from "@src/context/ApplicationContext";
import ClientLogsService from "@src/services/client-logs.service";
import { Skeleton } from "@uhg-abyss/web/ui/Skeleton";
import { type CSSProperties, useEffect, useRef, useState } from "react";

const DEFAULT_REVEAL_TIMEOUT_MS = 2000;
const DEFAULT_FADE_DURATION_MS = 250;

interface InvocaPhoneNumberProps {
  phoneNumber?: string;
  className?: string;
  style?: CSSProperties;
  revealTimeoutMs?: number;
  fadeDurationMs?: number;
}

const hasVisibleTrackedPhoneNumber = (
  element: HTMLElement | null,
  canonicalPhoneNumber: string,
): boolean => {
  if (!element) {
    return false;
  }

  const currentText = element.textContent?.trim();
  return Boolean(currentText && currentText !== canonicalPhoneNumber);
};

export const InvocaPhoneNumber = ({
  phoneNumber = PHONE_NUMBER,
  className,
  style,
  revealTimeoutMs = DEFAULT_REVEAL_TIMEOUT_MS,
  fadeDurationMs = DEFAULT_FADE_DURATION_MS,
}: InvocaPhoneNumberProps) => {
  const appContext = useOptionalApplicationContext();
  const userUuid = appContext?.data?.userUuid ?? "unknown";
  const userUuidRef = useRef(userUuid);
  const [isVisible, setIsVisible] = useState(false);
  const numberRef = useRef<HTMLElement>(null);

  useEffect(() => {
    userUuidRef.current = userUuid;
  }, [userUuid]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const mountTime = performance.now();

    const showNumber = (reason: "swapped" | "timeout") => {
      const elapsed = Math.round(performance.now() - mountTime);
      if (reason === "swapped") {
        ClientLogsService.logWebAction(
          userUuidRef.current,
          "InvocaPhoneNumber.useEffect",
          `Number swapped in ${elapsed}ms`,
          undefined,
          {},
        );
      } else {
        ClientLogsService.logWebAction(
          userUuidRef.current,
          "InvocaPhoneNumber.useEffect",
          `Fallback timeout reached after ${elapsed}ms - showing original number`,
          undefined,
          {},
        );
      }
      setIsVisible(true);
    };

    const maybeShowTrackedNumber = () => {
      if (hasVisibleTrackedPhoneNumber(numberRef.current, phoneNumber)) {
        showNumber("swapped");
        return true;
      }

      return false;
    };

    if (maybeShowTrackedNumber()) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (maybeShowTrackedNumber()) {
        observer.disconnect();
        window.clearTimeout(revealTimeout);
      }
    });

    if (numberRef.current) {
      observer.observe(numberRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const revealTimeout = window.setTimeout(() => {
      observer.disconnect();
      showNumber("timeout");
    }, revealTimeoutMs);

    return () => {
      observer.disconnect();
      window.clearTimeout(revealTimeout);
    };
  }, [phoneNumber, revealTimeoutMs]);

  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
      }}
    >
      {!isVisible && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "inline-flex",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <Skeleton width="100%" height="20em" />
        </span>
      )}
      <span
        ref={numberRef}
        className={className}
        data-obc-invoca-phone-number="true"
        aria-hidden={!isVisible}
        style={{
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${fadeDurationMs}ms ease-in-out`,
          ...style,
        }}
      >
        {phoneNumber}
      </span>
    </span>
  );
};
